import { Box, Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as RadioEmptyIcon } from "components/Icons/radio-empty.svg";
import { ReactComponent as RadioIcon } from "components/Icons/radio.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { ReactNode } from "react";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    item: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      padding: theme.spacing(8),
      border: `1px solid ${theme.gray200}`,
      borderRadius: "8px",
      cursor: "pointer",
      height: "100%",
    },
    activeItem: {
      backgroundColor: theme.white500,
      border: `1px solid ${theme.primary700}`,
      boxShadow: "0px 1px 1px rgba(154, 147, 147, 0.25)",
    },
    rowDisplay: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    label: {
      alignItems: "center",
      color: theme.gray800,
      display: "flex",
      fontSize: "14px",
      fontWeight: 500,
      gap: theme.spacing(4),
      lineHeight: "24px",
    },
    description: {
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "24px",
      color: theme.gray500,
      whiteSpace: "break-spaces",
    },
    checkedIcon: {
      color: theme.primary500,
    },
    blankIcon: {
      color: theme.gray400,
    },
    disabled: {
      opacity: 0.56,
      cursor: "not-allowed",
    },
  }),
);

type Props = {
  activeItem: boolean;
  label: string | ReactNode;
  description?: string;
  disabled?: boolean;
  tooltip?: JSX.Element;
  onClick?: () => void;
  "data-test-id"?: string;
};

export function ItemTypeCheckbox({
  activeItem,
  label,
  description,
  disabled,
  tooltip,
  onClick,
  "data-test-id": dataTestId,
}: Props) {
  const classes = useStyles();

  return (
    <Box
      className={`${classes.item} ${activeItem ? classes.activeItem : ""} ${
        disabled ? classes.disabled : ""
      }`}
      onClick={disabled ? () => null : onClick}
      data-test-id={`${dataTestId}-box`}
    >
      <Box className={classes.rowDisplay}>
        <Typography
          className={classes.label}
          data-test-id={`${dataTestId}-label`}
        >
          {label} {tooltip && tooltip}
        </Typography>
        {activeItem ? (
          <RadioIcon
            className={classes.checkedIcon}
            data-test-id={`${dataTestId}-checkbox`}
          />
        ) : (
          <RadioEmptyIcon
            className={classes.blankIcon}
            data-test-id={`${dataTestId}-blank-checkbox`}
          />
        )}
      </Box>
      <Typography className={classes.description}>{description}</Typography>
    </Box>
  );
}
